import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { motion } from 'framer-motion';
import { AppTheme } from '@design-system/theme/types';
import { Typography } from '@design-system/atoms/typography';
import { IconButton } from '@design-system/atoms/icon-button';
import { IconButtonContainer } from '@design-system/atoms/icon-button/icon-button.styles';

// Theme variables extraction
const getNavColors = (theme: AppTheme) => ({
  text: theme.colors.technical.gray[60],
  activeText: theme.colors.primary.normal,
  activeBackground: theme.colors.secondary.normal,
  background: theme.colors.technical['adaptive-reverse'],
  mutedText: theme.colors.technical.gray[30],
  focusBorder: theme.colors.primary.normal,
});

// Common mixins
const setFocusibleStyles = (theme: AppTheme) => css`
  outline: 2px solid ${getNavColors(theme).focusBorder};
  outline-offset: -2px;
`;

const collapsedLayoutMixin = (isCollapsed: boolean) => css`
  justify-content: ${isCollapsed ? 'center' : 'flex-start'};
  margin: ${isCollapsed ? '0 auto' : '0'};
`;

const collapsedPaddingMixin = (isCollapsed: boolean) => css`
  padding: ${isCollapsed ? '12px 0' : '12px 0 12px 16px'};
`;

// Base interactive element
const InteractiveElement = styled.button<{ isFocusVisible?: boolean }>`
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => getNavColors(theme).text};
  background-color: transparent;

  ${({ isFocusVisible, theme }) => isFocusVisible && setFocusibleStyles(theme)};

  &:hover {
    color: ${({ theme }) => getNavColors(theme).activeText};
  }

  &:active {
    color: ${({ theme }) => theme.colors.primary.medium};
  }
`;

// Base item for navigation elements
const BaseItem = styled(InteractiveElement)<{ shouldHighlight?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  width: 100%;
`;

// Typography component with highlight option
const ItemLabel = styled(Typography)<{ shouldHighlight?: boolean }>`
  display: block;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-align: left;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ shouldHighlight, theme }) =>
    shouldHighlight &&
    css`
      color: ${getNavColors(theme).activeText};
    `}
`;

// Main components
export const SidebarContainer = styled(motion.div)`
  position: sticky;
  z-index: 11;
  top: 0;
  left: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100vh;
  gap: 16px;
  background-color: ${({ theme }) => getNavColors(theme).background};
`;

export const LogoContainer = styled.div<{ $isNavigationCollapsed: boolean }>`
  position: relative;
  padding: 40px ${({ $isNavigationCollapsed }) => ($isNavigationCollapsed ? '21px' : '24px')} 24px;
  overflow: hidden;

  img {
    display: block;
    max-height: 30px;
  }
`;

export const UserSection = styled.div<{ $isNavigationCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ $isNavigationCollapsed }) => collapsedLayoutMixin($isNavigationCollapsed)}
  padding: ${({ $isNavigationCollapsed }) => ($isNavigationCollapsed ? '0 16px' : '0 28px')};
`;

export const NavSection = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px;
  overflow-y: auto;
`;

export const NavItemContainer = styled.div<{ $isNavigationCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ $isNavigationCollapsed }) =>
    $isNavigationCollapsed &&
    css`
      align-items: center;
    `}
`;

export const NavItem = styled(BaseItem)<{ $isNavigationCollapsed: boolean }>`
  min-width: 56px;
  ${({ $isNavigationCollapsed }) => collapsedLayoutMixin($isNavigationCollapsed)}
  ${({ $isNavigationCollapsed }) => collapsedPaddingMixin($isNavigationCollapsed)};
  background-color: ${({ shouldHighlight, theme }) =>
    shouldHighlight ? getNavColors(theme).activeBackground : 'transparent'};
`;

export const NavItemLabel = ItemLabel;

export const NavItemBadge = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: 'General Sans';
  font-size: 10px;
  font-weight: 500;
  line-height: 12px; /* 120% */
  background-color: ${({ theme }) => theme.colors.primary.normal};
  color: ${({ theme }) => theme.colors.background.light};
`;

export const SubItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 0 0 36px;
`;

export const SubItem = styled(BaseItem)<{ shouldHighlight?: boolean }>`
  background-color: ${({ shouldHighlight, theme }) =>
    shouldHighlight ? getNavColors(theme).activeBackground : 'transparent'};
`;

export const SubItemLabel = ItemLabel;

export const SidebarFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
`;

export const Version = styled.div`
  font-size: 12px;
  color: ${({ theme }) => getNavColors(theme).mutedText};
  margin-top: 8px;
`;

export const ResizeButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  padding: 6px;
  top: 50%;
  right: 0;
  transform: translate(100%, -50%);

  ${IconButtonContainer} {
    width: 20px;
    height: 20px;
  }
`;

export const HoverButton = styled.div`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
  z-index: 1;

  ${ResizeButtonContainer}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const DefaultButton = styled.div`
  opacity: 1;
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.2s ease;
  pointer-events: auto;

  ${ResizeButtonContainer}:hover & {
    opacity: 0;
    pointer-events: none;
  }
`;

export const ResizeButton = styled(IconButton)<{ $isNavigationCollapsed: boolean }>`
  transform: ${({ $isNavigationCollapsed }) => (!$isNavigationCollapsed ? 'rotate(90deg)' : 'rotate(-90deg)')};
  transition: transform 0.3s ease;
`;

export const ActionMenuContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: fixed;
  z-index: 1000;
  background-color: ${({ theme }) => getNavColors(theme).background};
  color: ${({ theme }) => getNavColors(theme).text};
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 180px;
`;

export const ActionMenuItem = styled(BaseItem)<{ isActive: boolean }>`
  font-size: 14px;
  border-radius: 4px;
  padding: 10px 12px;
`;

export const ActionMenuHeader = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.technical.adaptive};
  font-weight: 700;
  margin-bottom: 4px;
`;
